/**
 * Every display format on the Sales Insights page. Rows build their text only
 * from these helpers, so a dollar amount or a percentage looks the same
 * everywhere and a formatting fix lands in one place.
 *
 * Rounding, signs, grouping and K/M/B abbreviation come from the built-in
 * Intl.NumberFormat. What's ours: the placeholders, which digits each unit
 * shows, and a true minus sign.
 */

/** Shown when a value is missing (no date, no amount, or not a real number). */
export const MISSING = "—";

/** Shown for growth when last year had no sales, so there's nothing to compare. */
export const NO_COMPARISON = "—";

/** Shown when a text field (terms, salesperson) is empty in Eclipse. */
export const BLANK = "(blank)";

const LOCALE = "en-US";

// Whole dollars: "$9,999"
const WHOLE_DOLLARS = new Intl.NumberFormat(LOCALE, {
  style: "currency", // "$" prefix
  currency: "USD", // US dollars
  maximumFractionDigits: 0, // no cents
  signDisplay: "negative", // minus for negatives, nothing for zero or −0
});

// Thousands: "$21.4K"
const THOUSANDS = new Intl.NumberFormat(LOCALE, {
  style: "currency", // "$" prefix
  currency: "USD", // US dollars
  notation: "compact", // K / M / B suffix, picked after rounding
  minimumFractionDigits: 1, // always one decimal, "$21.0K"
  maximumFractionDigits: 1, // never more than one
  signDisplay: "negative", // minus for negatives only
});

// Millions and up: "$1.06M", "$2.50B"
const MILLIONS = new Intl.NumberFormat(LOCALE, {
  style: "currency", // "$" prefix
  currency: "USD", // US dollars
  notation: "compact", // M / B / T suffix, picked after rounding
  minimumFractionDigits: 2, // always two decimals, "$1.00M"
  maximumFractionDigits: 2, // never more than two
  signDisplay: "negative", // minus for negatives only
});

// Changes as percents: "+3.4%"
const GROWTH = new Intl.NumberFormat(LOCALE, {
  style: "percent", // takes a fraction: 0.034 → 3.4%
  minimumFractionDigits: 1, // always one decimal
  maximumFractionDigits: 1, // never more than one
  signDisplay: "exceptZero", // "+" or "−", nothing when it rounds to zero
});

// Changes as whole percents, for tight spaces: "+6%"
const GROWTH_WHOLE = new Intl.NumberFormat(LOCALE, {
  style: "percent", // takes a fraction: 0.06 → 6%
  maximumFractionDigits: 0, // whole percents
  signDisplay: "exceptZero", // "+" or "−", nothing when it rounds to zero
});

// Differences in points: "+6.6"
const POINTS = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1, // always one decimal
  maximumFractionDigits: 1, // never more than one
  signDisplay: "exceptZero", // "+" or "−", nothing when it rounds to zero
});

// Thresholds and shares: "20%"
const PERCENT = new Intl.NumberFormat(LOCALE, {
  style: "percent", // takes a fraction: 0.2 → 20%
  maximumFractionDigits: 0, // whole percents
  signDisplay: "negative", // minus for negatives only
});

// Counts: "1,234"
const COUNT = new Intl.NumberFormat(LOCALE, {
  maximumFractionDigits: 0, // whole numbers
  signDisplay: "negative", // minus for negatives only
});

// Rates: "17.7"
const RATE = new Intl.NumberFormat(LOCALE, {
  minimumFractionDigits: 1, // always one decimal
  maximumFractionDigits: 1, // never more than one
  useGrouping: false, // "1234.5", not "1,234.5"
  signDisplay: "negative", // minus for negatives only
});

// Dates: "Sep 28, 2026"
const DATE = new Intl.DateTimeFormat(LOCALE, {
  month: "short", // "Sep"
  day: "numeric", // "28"
  year: "numeric", // "2026"
  timeZone: "UTC", // dates are read as UTC midnight, so print in UTC too
});

/** A real, finite number (not null, undefined, NaN or Infinity). */
function isNumber(x: number | null | undefined): x is number {
  return typeof x === "number" && Number.isFinite(x);
}

/** Intl prints a hyphen for minus; use the true minus sign, which reads better. */
function trueMinus(text: string): string {
  return text.replace("-", "−");
}

/**
 * Dollars, abbreviated like the data sources doc: $950, $21.4K, $1.06M, $2.50B.
 * Whole dollars under $10,000; thousands with one decimal; millions and up
 * with two.
 */
export function money(x: number | null | undefined): string {
  // No amount, or not a real number
  if (!isNumber(x)) return MISSING;
  // Whole dollars, unless that would reach five digits ($10,000)
  if (Math.abs(Math.round(x)) < 1e4) return trueMinus(WHOLE_DOLLARS.format(x));
  // Thousands, unless rounding carried it up to millions ($999,950 → $1.0M)
  const thousands = THOUSANDS.formatToParts(x);
  if (thousands.find((p) => p.type === "compact")?.value === "K") {
    return trueMinus(thousands.map((p) => p.value).join(""));
  }
  // Millions and up. Between $999,950 and $999,995 two decimals would drop back
  // to "$999.95K", but one decimal already rounded it up, so it's $1.00M
  const millions = MILLIONS.formatToParts(x);
  if (millions.find((p) => p.type === "compact")?.value === "K") {
    return trueMinus(MILLIONS.format(Math.sign(x) * 1e6));
  }
  return trueMinus(millions.map((p) => p.value).join(""));
}

/** Whole dollars, never abbreviated: $551,295. For headline totals. */
export function dollars(x: number | null | undefined): string {
  // No amount, or not a real number
  return isNumber(x) ? trueMinus(WHOLE_DOLLARS.format(x)) : MISSING;
}

/** A change as a signed percent: +3.4%, −5.0%. Takes a fraction (0.034). */
export function growth(x: number | null | undefined): string {
  // No prior period to compare against, or not a real number
  return isNumber(x) ? trueMinus(GROWTH.format(x)) : NO_COMPARISON;
}

/** Changes bigger than this either way (1000 = 100,000%) show as ">100,000%". */
export const GROWTH_DISPLAY_CAP = 1000;

/**
 * A change as a signed whole percent: +6%, −14%. Takes a fraction (0.06).
 * Beyond ±100,000% it shows ">100,000%" or "<−100,000%", so it fits a pill.
 */
export function growthWhole(x: number | null | undefined): string {
  // No prior period to compare against, or not a real number
  if (!isNumber(x)) return NO_COMPARISON;
  // Bigger than the cap: say so instead of printing the whole number
  if (x > GROWTH_DISPLAY_CAP) return `>${PERCENT.format(GROWTH_DISPLAY_CAP)}`;
  if (x < -GROWTH_DISPLAY_CAP) return `<${trueMinus(PERCENT.format(-GROWTH_DISPLAY_CAP))}`;
  return trueMinus(GROWTH_WHOLE.format(x));
}

/** A difference in percentage points: +6.6 pts, −72.2 pts. */
export function points(x: number | null | undefined): string {
  // No difference to show, or not a real number
  return isNumber(x) ? `${trueMinus(POINTS.format(x))} pts` : NO_COMPARISON;
}

/** A threshold or share as a plain whole percent: 20%. Takes a fraction (0.2). */
export function percent(x: number | null | undefined): string {
  return isNumber(x) ? trueMinus(PERCENT.format(x)) : MISSING;
}

/** A count with commas: 1,234. */
export function count(x: number | null | undefined): string {
  return isNumber(x) ? trueMinus(COUNT.format(x)) : MISSING;
}

/** A rate with one decimal: 17.7. */
export function rate(x: number | null | undefined): string {
  return isNumber(x) ? trueMinus(RATE.format(x)) : MISSING;
}

/**
 * A YYYY-MM-DD date as "Sep 28, 2026", without shifting the day by time zone.
 * Anything else, including impossible dates like 2026-02-30, shows as missing.
 */
export function date(iso: string | null | undefined): string {
  // Only accept the exact YYYY-MM-DD shape
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return MISSING;
  // Read it as midnight UTC
  const parsed = new Date(`${iso}T00:00:00Z`);
  // Date quietly rolls 2026-02-30 over to Mar 2; reject anything that moved
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== iso) {
    return MISSING;
  }
  return DATE.format(parsed);
}

/** Free text from Eclipse, or a visible placeholder when it's empty. */
export function text(x: string | null | undefined): string {
  return x?.trim() ? x : BLANK;
}
